import type { JSX } from 'preact'

export interface RowActivationProps {
  role: 'button'
  tabIndex: 0
  onClick: () => void
  onKeyDown: (event: JSX.TargetedKeyboardEvent<HTMLElement>) => void
  onKeyUp: (event: JSX.TargetedKeyboardEvent<HTMLElement>) => void
}

/**
 * The props a list row needs to behave like a button for a keyboard as well as a pointer
 * (WCAG 2.1.1 — PRODUCT.md sets 2.2 AA as the floor). A bare `onClick` on a `<div>` or `<li>` is
 * reachable by mouse only.
 *
 * Returns `null` when the row has no action — mock mode, or a row with nothing to open. The caller
 * spreads nothing and must also drop the hover affordance, or the row still advertises a click it
 * will not answer.
 *
 * Spread this on an element INSIDE the `<li>`, not on the `<li>` itself: `role="button"` on a list
 * item replaces its `listitem` role and breaks the list semantics of the surrounding `<ul>`.
 *
 * Takes no aria-label deliberately. A role="button" element's accessible name comes from its
 * contents, and a short override ("Open Nicole Gamo") is what a screen reader announces INSTEAD of
 * the row — company, job title, lead count and signal all disappear. The row's own text is the
 * better name.
 *
 * Enter fires on keydown and Space on keyup, which is what a native `<button>` does. Space also has
 * to take the keydown, or the page scrolls under the row; and activating it on keydown instead would
 * fire once per repeat for as long as the key is held.
 */
export function rowActivation(onActivate: (() => void) | null | undefined): RowActivationProps | null {
  if (!onActivate) {
    return null
  }

  return {
    role: 'button',
    tabIndex: 0,
    onClick: onActivate,
    onKeyDown: (event: JSX.TargetedKeyboardEvent<HTMLElement>) => {
      if (event.key === 'Enter') {
        // Enter would otherwise submit an enclosing form.
        event.preventDefault()
        onActivate()
        return
      }
      if (event.key === ' ') {
        // Held down this repeats; taking it here only stops the scroll, and keyup does the work.
        event.preventDefault()
      }
    },
    onKeyUp: (event: JSX.TargetedKeyboardEvent<HTMLElement>) => {
      if (event.key !== ' ') {
        return
      }
      event.preventDefault()
      onActivate()
    },
  }
}
